'use client';

import { useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import type { CSSProperties } from 'react';
import { ChannelDiscord } from '@/app/components/icons/ChannelDiscord';
import { ChannelWhiteWeChat } from '@/app/components/icons/ChannelWhiteWeChat';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { QRCODE_URL, SITE } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { CHINA_TESTIMONIALS, GLOBAL_TESTIMONIALS } from '../CreatorsMarquee';
import { ParticleSphere } from './ParticleSphere';

interface OrbitAvatar {
  src: string;
  /** 起始角度（deg），0 为正上方。 */
  angle: number;
}

interface Orbit {
  /** 轨道直径。基准=1440 设计稿，md/sm 是 max-width 语义（本仓库断点反向）。 */
  size: string;
  /** 绕一圈的秒数。 */
  duration: number;
  avatars: OrbitAvatar[];
}

/** 与 CreatorsMarquee 共用同一份数据，按 SITE 选套，取前 8 条的 avatar。 */
const TESTIMONIALS = SITE === 'china' ? CHINA_TESTIMONIALS : GLOBAL_TESTIMONIALS;
const AVATARS = TESTIMONIALS.slice(0, 8).map((t) => t.author.avatar);

/** 三条轨道，由内到外。头像动态取自 CreatorsMarquee 数据源，按 SITE 切换。 */
const ORBITS: Orbit[] = [
  {
    size: 'h-[49.5rem] w-[49.5rem] sm:h-[27rem] sm:w-[27rem] md:h-[36rem] md:w-[36rem]',
    duration: 28,
    avatars: [
      { src: AVATARS[0], angle: -60 },
      { src: AVATARS[1], angle: 60 },
    ],
  },
  {
    size: 'h-[67.5rem] w-[67.5rem] sm:h-[36rem] sm:w-[36rem] md:h-[48.6rem] md:w-[48.6rem]',
    duration: 38,
    avatars: [
      { src: AVATARS[2], angle: 0 },
      { src: AVATARS[3], angle: -90 },
    ],
  },
  {
    size: 'h-[85.5rem] w-[85.5rem] sm:h-[45rem] sm:w-[45rem] md:h-[61.2rem] md:w-[61.2rem]',
    duration: 48,
    avatars: [
      { src: AVATARS[4], angle: -60 },
      { src: AVATARS[5], angle: 0 },
      { src: AVATARS[6], angle: 60 },
      { src: AVATARS[7], angle: 120 },
    ],
  },
];

const DISCORD_URL = 'https://discord.gg/3pDU8fmQ8Y';

/**
 * 创意社区屏：环绕轨道（社区用户头像）+ 中心粒子球 + 标题/说明/双 CTA。
 *
 * 每个头像外层绕轨道公转，内层再反向自转同样时长 —— 两者抵消，头像始终保持正向不歪头。
 * 轨道只露上半圆（容器底部对齐 + translate-y-1/2）。底部叠一层 #373972 的 0→80% 渐变蒙版，
 * 与文案层分离，保证文字在轨道之上仍然清晰。
 *
 * 尺寸说明：原始组件是 Tailwind v4 写法（w-110 / md:w-180 这类按需 spacing），
 * 本仓库是 v3.4 且未扩展 spacing 上限（默认到 96），那些类名编译不出 CSS，
 * 因此改成 arbitrary value；且本仓库 md/sm 是 max-width 语义，尺寸随屏幕变小递减。
 */
export const CommunityOrbit = () => {
  const t = useTranslations('home3.community');
  const tJoin = useTranslations('home3.joinOur');
  // 动画走内联 style，优先级高于样式表，@media 里的 animation:none 压不住（且 lint 不允许 !important），
  // 所以降级判断放在 JS：reduced-motion 时不挂 animation，只用静态 transform 摆到起始角度。
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="relative flex h-[49.5rem] w-full justify-center overflow-hidden sm:h-[27rem] md:h-[36rem]">
      {/* 中心粒子球 */}
      <div className="-translate-x-1/2 pointer-events-none absolute bottom-0 left-1/2 z-10 aspect-square w-[33.75rem] translate-y-1/2 sm:w-[18rem] md:w-[23.4rem]">
        <ParticleSphere />
      </div>

      {ORBITS.map((orbit, index) => {
        const isClockwise = index % 2 === 0;
        const orbitAnim = isClockwise ? 'vidu-orbit-cw' : 'vidu-orbit-ccw';
        const counterAnim = isClockwise ? 'vidu-counter-cw' : 'vidu-counter-ccw';

        // 每个头像在对侧再镜像一个，让轨道上的分布对称（镜像件纯装饰）
        const avatars = [
          ...orbit.avatars.map((a) => ({ ...a, mirrored: false })),
          ...orbit.avatars.map((a) => ({ ...a, angle: a.angle + 180, mirrored: true })),
        ];

        return (
          <div
            key={orbit.size}
            className={`-translate-x-1/2 absolute bottom-0 left-1/2 translate-y-1/2 rounded-full border border-white/[0.14] ${orbit.size}`}
          >
            {avatars.map((avatar) => (
              <div
                key={`${orbit.size}-${avatar.src}-${avatar.angle}-${avatar.mirrored ? 'm' : 'o'}`}
                className="-ml-[1.44rem] sm:-ml-[0.9rem] md:-ml-[1.08rem] absolute top-0 left-1/2 flex h-1/2 origin-bottom flex-col items-center justify-start"
                style={
                  prefersReducedMotion
                    ? { transform: `rotate(${avatar.angle}deg)` }
                    : ({
                        '--start-angle': `${avatar.angle}deg`,
                        animation: `${orbitAnim} ${orbit.duration}s linear infinite`,
                      } as CSSProperties)
                }
              >
                <div
                  className="-mt-[1.44rem] sm:-mt-[0.9rem] md:-mt-[1.08rem] relative z-10 overflow-hidden rounded-full border border-white/20 bg-black"
                  style={
                    prefersReducedMotion
                      ? { transform: `rotate(${-avatar.angle}deg)` }
                      : ({
                          '--counter-offset': `${-avatar.angle}deg`,
                          animation: `${counterAnim} ${orbit.duration}s linear infinite`,
                        } as CSSProperties)
                  }
                >
                  <img
                    src={avatar.src}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    decoding="async"
                    width={64}
                    height={64}
                    className="size-[2.88rem] object-cover sm:size-[1.8rem] md:size-[2.16rem]"
                  />
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {/* 底部蒙版：#373972 由透明渐变到 64%，压住轨道下缘 */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-3/4 bg-[linear-gradient(to_bottom,rgba(55,57,114,0)_0%,rgba(55,57,114,0.64)_100%)]" />

      {/* 文案与 CTA：顶部对齐，压在轨道与蒙版之上 */}
      <div className="absolute inset-x-0 top-0 z-30 flex flex-col items-center px-10 pt-14 text-center sm:px-4 sm:pt-6 md:px-6 md:pt-8">
        {/* 标题 / 副标题与第四屏 bento 完全同款（字号、字重、行高、字距、透明度） */}
        <h2 className="mx-auto max-w-[46rem] text-center font-semibold text-[3.5rem] text-white leading-[1.05] tracking-tight [text-wrap:balance] md:text-[2.5rem] lg:text-[3rem]">
          {t('title')}
        </h2>
        <p className="mt-4 text-center font-normal text-[16px] text-system-white64 md:mt-3">{t('description')}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-3 md:mt-6">
          {SITE === 'global' && (
            <>
              <button
                type="button"
                className="flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-8 font-semibold text-[14px] text-black transition hover:bg-white/90 sm:h-10 sm:px-5"
                onClick={() => {
                  reportEvent('element_click', { element_name: 'Community-Join Discord' });
                  openOrRedirect(DISCORD_URL);
                }}
              >
                <ChannelDiscord className="size-5" />
                {tJoin('joinDiscord')}
              </button>
              <ClientLink
                href="/cpp"
                className="flex h-12 cursor-pointer items-center justify-center whitespace-nowrap rounded-full border-[2px] border-system-white24 px-8 font-semibold text-[14px] text-white transition hover:bg-white/10 sm:h-10 sm:px-5"
                onClick={() => {
                  reportEvent('element_click', { element_name: 'Community-Apply for CPP' });
                }}
              >
                {tJoin('applyForCPP')}
              </ClientLink>
            </>
          )}
          {SITE === 'china' && (
            <div
              className="h-12 group pointer-events-auto relative flex cursor-pointer items-center justify-center gap-1 rounded-full bg-[#50C365] px-6 py-2 md:py-[10px]"
              onClick={() => {
                reportEvent('element_click', {
                  element_name: 'Community-Join Wechat',
                });
              }}
            >
              <ChannelWhiteWeChat className="size-6" />
              <div className="font-semibold text-[14px] text-white">{tJoin('joinWechat')}</div>
              <div className="absolute top-full z-10 hidden pt-2 group-hover:block">
                <div className="h-[248px] w-[216px] rounded-12 border border-system-white12 bg-system-bg02 px-2 py-3">
                  <div className="mx-auto h-[200px] w-[200px]">
                    <ImageWithPlaceholder
                      src={QRCODE_URL}
                      alt="微信群二维码"
                      className="h-full w-full rounded-6 bg-[#3C4352]"
                    />
                  </div>
                  <p className="mt-2 text-center font-[400] text-system-white64 text-xs tracking-[0]">
                    加入讨论群，了解最新动态
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityOrbit;
